import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { invoke } from '@tauri-apps/api/core';
import { tell, arrive } from './utils/visit';
import { micWords } from './utils/micWords';
import { AudioEngine } from './utils/audioEngine';
import { PitchResult } from './utils/pitchDetection';
import RadialScope from './components/RadialScope';
import WritePanel from './components/WritePanel';
import CombinedView, { RangeMode, ViewState, ExtraTrace } from './components/CombinedView';
import { FrameStore } from './utils/frameStore';
import Piano, { VoiceType, VOICE_LABELS } from './components/Piano';
import { extractContour } from './utils/contourExtract';
import { InputOptions } from './utils/audioEngine';
import { spectralStats, brightness, breathiness, pitchWobbleCents, intervalInfo, IntervalInfo, partialsOf, roughness } from './utils/timbre';
import HarmonicRecipe from './components/HarmonicRecipe';
import NotesRinging from './components/NotesRinging';
import FormantsPanel from './components/FormantsPanel';
import './App.css';

type InputMode = 'voice' | 'instrument';
const INPUT_STORAGE_KEY = 'songbird.input';
const SOURCES_STORAGE_KEY = 'songbird.sources';
function loadInputPrefs(): { deviceId: string | null; mode: InputMode; channel: number | undefined } {
  try {
    const raw = localStorage.getItem(INPUT_STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      return {
        deviceId: typeof p.deviceId === 'string' ? p.deviceId : null,
        mode: p.mode === 'instrument' ? 'instrument' : 'voice',
        channel: typeof p.channel === 'number' ? p.channel : undefined,
      };
    }
  } catch { /* ignore */ }
  return { deviceId: null, mode: 'voice', channel: undefined };
}
function saveInputPrefs(deviceId: string | null, mode: InputMode, channel: number | undefined) {
  try { localStorage.setItem(INPUT_STORAGE_KEY, JSON.stringify({ deviceId, mode, channel })); } catch { /* ignore */ }
}

// Extra live sources (a second mic, the other channel of an interface, ...)
interface ExtraSource {
  id: string;
  label: string;
  color: string;            // "r, g, b"
  deviceId: string | null;
  channel: number | undefined;
  processing: boolean;
}
interface ExtraRuntime {
  engine: AudioEngine;
  frames: FrameStore;
  pitchRef: { current: PitchResult | null };
  spectrumRef: { current: Float32Array | null };
}
interface ExtraLive {
  pitch: PitchResult | null;
  interval: IntervalInfo | null;  // relative to the primary source
  friction: number;               // 0 smooth .. 1 rough, against the primary source
}
const EXTRA_COLORS = ['127, 184, 221', '209, 125, 195', '227, 185, 85', '208, 224, 122', '182, 173, 196'];
const CHANNEL_LABELS: Record<string, string> = { mix: 'Mix', '0': 'Input 1 (L)', '1': 'Input 2 (R)' };
function loadExtraSources(): ExtraSource[] {
  try {
    const raw = localStorage.getItem(SOURCES_STORAGE_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) return arr.filter(s => s && typeof s.id === 'string' && typeof s.label === 'string');
    }
  } catch { /* ignore */ }
  return [];
}
function saveExtraSources(list: ExtraSource[]) {
  try { localStorage.setItem(SOURCES_STORAGE_KEY, JSON.stringify(list)); } catch { /* ignore */ }
}
// Short device name for the toolbar chip: "Scarlett 4i4 USB (1235:8212)" → "Scarlett 4i4"
function shortDeviceName(label: string): string {
  const cleaned = label.replace(/\s*\([0-9a-f]{4}:[0-9a-f]{4}\)\s*$/i, '').replace(/^(Default|Communications)\s*-\s*/i, '').trim();
  const words = cleaned.split(/\s+/).slice(0, 2).join(' ');
  return words.length > 16 ? words.slice(0, 15) + '…' : words;
}

const HISTORY_LEN = 500;
const IS_TAURI = typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const midiToHz = (midi: number) => Math.round(440 * Math.pow(2, (midi - 69) / 12) * 10) / 10;

// Slim inline metronome for the toolbar. Only tempo + on/off + fixed 4/4
// for now; if we need time-signature switching back, put it in a gear menu.
function InlineMetro({ bpm, active, onBpmChange, onToggle }: {
  bpm: number; active: boolean;
  onBpmChange: (bpm: number) => void; onToggle: () => void;
}) {
  const [text, setText] = useState(String(bpm));
  useEffect(() => setText(String(bpm)), [bpm]);
  const commit = () => {
    const n = parseInt(text, 10);
    if (!isNaN(n)) onBpmChange(Math.max(20, Math.min(400, n)));
    else setText(String(bpm));
  };
  return (
    <div className={`metro-inline${active ? ' on' : ''}`} title="Metronome: tempo + on/off. Type a BPM and press Enter.">
      <button
        className={`metro-dot-btn${active ? ' on' : ''}`}
        onClick={onToggle}
        title={active ? 'Stop metronome' : 'Start metronome'}
      ><span className="dot" /></button>
      <span className="metro-sym">♩=</span>
      <input
        type="text"
        inputMode="numeric"
        value={text}
        onChange={e => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') { commit(); (e.target as HTMLInputElement).blur(); } }}
        className="metro-bpm-input"
      />
      <span className="metro-sig">4/4</span>
    </div>
  );
}

interface PitchSample { t: number; p: PitchResult | null; }
interface Recording {
  id: string; label: string; pitches: PitchSample[];
  audioBlob: Blob | null; duration: number; createdAt: number; selected: boolean;
  mimeType?: string;      // set on imports so playback picks the right decoder
  imported?: boolean;     // distinguishes file imports from live recordings
  contourProgress?: number; // 0-1 while contour is extracting; 1 (or absent) when done
}
interface PianoEvent { midi: number; type: 'on' | 'off'; t: number; }
interface NoteSegment { hz: number; startMs: number; durationMs: number; }
interface PracticeScheme {
  id: string; label: string; events: PianoEvent[]; noteCount: number;
  segments: NoteSegment[]; totalDurationMs: number; createdAt: number; selected: boolean;
}

function buildSegments(events: PianoEvent[]): NoteSegment[] {
  const segments: NoteSegment[] = [];
  const pending = new Map<number, number>();
  for (const { midi, type, t } of events) {
    if (type === 'on') {
      pending.set(midi, t);
    } else if (type === 'off') {
      const startMs = pending.get(midi);
      if (startMs !== undefined) {
        pending.delete(midi);
        segments.push({ hz: 440 * Math.pow(2, (midi - 69) / 12), startMs, durationMs: Math.max(50, t - startMs) });
      }
    }
  }
  return segments.sort((a, b) => a.startMs - b.startMs);
}

function scheduleNotes(ctx: AudioContext, segments: NoteSegment[], audioStart: number) {
  for (const seg of segments) {
    const noteStart = audioStart + seg.startMs / 1000;
    const noteDur = Math.max(0.05, seg.durationMs / 1000 - 0.03);
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = seg.hz;
    osc.connect(gain);
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0, noteStart);
    gain.gain.linearRampToValueAtTime(0.2, noteStart + 0.02);
    gain.gain.setValueAtTime(0.2, noteStart + noteDur - 0.03);
    gain.gain.linearRampToValueAtTime(0, noteStart + noteDur);
    osc.start(noteStart);
    osc.stop(noteStart + noteDur + 0.01);
  }
}

const TIME_SIGS = [
  { num: 2, den: 4 }, { num: 3, den: 4 }, { num: 4, den: 4 },
  { num: 5, den: 4 }, { num: 6, den: 8 }, { num: 12, den: 8 },
];

const BPM_PRESETS = [
  { bpm: 40, label: 'Grave' }, { bpm: 50, label: 'Largo' },
  { bpm: 60, label: 'Adagio' }, { bpm: 72, label: 'Andante' },
  { bpm: 80, label: '' }, { bpm: 96, label: 'Moderato' },
  { bpm: 100, label: '' }, { bpm: 108, label: '' },
  { bpm: 112, label: 'Allegretto' }, { bpm: 120, label: '' },
  { bpm: 132, label: 'Allegro' }, { bpm: 144, label: '' },
  { bpm: 160, label: 'Vivace' }, { bpm: 176, label: '' },
  { bpm: 184, label: 'Presto' }, { bpm: 200, label: '' },
];

function MetroWidget({ bpm, timeSig, active, currentBeat, onToggle, onBpmChange, onTimeSigChange }: {
  bpm: number; timeSig: { num: number; den: number };
  active: boolean; currentBeat: number;
  onToggle: () => void;
  onBpmChange: (bpm: number) => void;
  onTimeSigChange: (num: number, den: number) => void;
}) {
  const [bpmText, setBpmText] = useState(String(bpm));
  const [bpmDropOpen, setBpmDropOpen] = useState(false);
  const [sigDropOpen, setSigDropOpen] = useState(false);
  const bpmDropRef = useRef<HTMLDivElement>(null);
  const sigDropRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setBpmText(String(bpm)); }, [bpm]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (bpmDropRef.current && !bpmDropRef.current.contains(e.target as Node)) setBpmDropOpen(false);
      if (sigDropRef.current && !sigDropRef.current.contains(e.target as Node)) setSigDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const commitBpm = () => {
    const parsed = parseInt(bpmText, 10);
    if (!isNaN(parsed)) {
      const clamped = Math.max(20, Math.min(400, parsed));
      onBpmChange(clamped);
      setBpmText(String(clamped));
    } else {
      setBpmText(String(bpm));
    }
  };

  const dotCount = Math.min(timeSig.num, 12);

  return (
    <div className={`metro-widget${active ? ' on' : ''}`} title="Metronome: tempo (BPM) and time signature. Beat lights flash below.">
      <div className="metro-hidden-label" aria-hidden style={{display:'none'}}>Metronome</div>
      <div className="metro-row-bpm">
        <span className="metro-note-sym">♩=</span>
        <div className="bpm-field-wrap" ref={bpmDropRef}>
          <input
            type="text"
            inputMode="numeric"
            className="bpm-input"
            value={bpmText}
            onChange={e => setBpmText(e.target.value)}
            onBlur={commitBpm}
            onKeyDown={e => { if (e.key === 'Enter') { commitBpm(); (e.target as HTMLInputElement).blur(); } }}
          />
          <button className="bpm-caret" onClick={() => setBpmDropOpen(o => !o)} title="BPM presets">▾</button>
          {bpmDropOpen && (
            <div className="bpm-preset-dropdown">
              {BPM_PRESETS.map(({ bpm: b, label }) => (
                <button
                  key={b}
                  className={`bpm-preset-opt${bpm === b ? ' active' : ''}`}
                  onClick={() => { onBpmChange(b); setBpmText(String(b)); setBpmDropOpen(false); }}
                >
                  <span className="bpm-preset-num">{b}</span>
                  {label && <span className="bpm-preset-lbl">{label}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="metro-row-sig">
        <div className="sig-field-wrap" ref={sigDropRef}>
          <button className="sig-current" onClick={() => setSigDropOpen(o => !o)}>
            {timeSig.num}/{timeSig.den}▾
          </button>
          {sigDropOpen && (
            <div className="sig-dropdown">
              {TIME_SIGS.map(({ num, den }) => (
                <button
                  key={`${num}/${den}`}
                  className={`sig-opt${timeSig.num === num && timeSig.den === den ? ' active' : ''}`}
                  onClick={() => { onTimeSigChange(num, den); setSigDropOpen(false); }}
                >{num}/{den}</button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="metro-row-ctrl">
        <button className={`btn-metro${active ? ' active' : ''}`} onClick={onToggle}>
          {active ? '■' : '▶'}
        </button>
        <div className="metro-dots">
          {Array.from({ length: dotCount }, (_, i) => (
            <div
              key={i}
              className={`metro-dot${active && i === currentBeat ? (i === 0 ? ' downbeat' : ' upbeat') : ''}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [running, setRunning] = useState(false);
  const [pitch, setPitch] = useState<PitchResult | null>(null);
  const [sampleRate, setSampleRate] = useState(44100);
  const [binCount, setBinCount] = useState(2048);
  const [error, setError] = useState<string | null>(null);
  // Why the microphone would not open, in words a visitor can act on.
  const [micProblem, setMicProblem] = useState<string | null>(null);
  const [rangeMode, setRangeMode] = useState<RangeMode>('follow');
  const [rangeMenuOpen, setRangeMenuOpen] = useState(false);

  // Input source: which device we listen to and whether to voice-process it.
  const [inputPrefs] = useState(loadInputPrefs);
  const [inputId, setInputId] = useState<string | null>(inputPrefs.deviceId);
  const [inputMode, setInputMode] = useState<InputMode>(inputPrefs.mode);
  const [inputChannel, setInputChannel] = useState<number | undefined>(inputPrefs.channel);
  const [inputLabel, setInputLabel] = useState('');
  const [inputChannels, setInputChannels] = useState(1);

  // Extra sources
  const [extraSources, setExtraSources] = useState<ExtraSource[]>([]);
  const [extraLive, setExtraLive] = useState<Record<string, ExtraLive>>({});
  const extraRtRef = useRef<Map<string, ExtraRuntime>>(new Map());
  const extrasRestoredRef = useRef(false);
  const [sourceMenuOpen, setSourceMenuOpen] = useState(false);
  const sourceMenuRef = useRef<HTMLDivElement>(null);      // the button wrapper
  const sourceMenuPopRef = useRef<HTMLDivElement>(null);   // the portalled menu
  const [newSrc, setNewSrc] = useState<{ deviceId: string | null; channel: number | undefined; processing: boolean; label: string }>({
    deviceId: null, channel: undefined, processing: false, label: 'Guitar',
  });
  const [inputs, setInputs] = useState<MediaDeviceInfo[]>([]);
  const [inputMenuOpen, setInputMenuOpen] = useState(false);
  const inputMenuRef = useRef<HTMLDivElement>(null);

  // Timbre meters (updated ~12×/s from the analysis refs)
  const [meters, setMeters] = useState({ bright: 0, breath: 0, vibrato: 0 });

  // Pause + scrub. The tracker draws from a 30 s frame store; pausing freezes
  // the view time and the scrub slider moves it back through the buffer.
  const [paused, setPaused] = useState(false);
  const [scrubBack, setScrubBack] = useState(0); // seconds behind the pause point
  const viewStateRef = useRef<ViewState>({ paused: false, pauseTimeMs: 0, scrubBackMs: 0 });
  const framesRef = useRef<FrameStore | null>(null);
  const framesHiRef = useRef<FrameStore | null>(null);
  const hiFrameCounterRef = useRef(0);
  // What the rail panels analyse: live data, or the paused / hovered frame. Filled by CombinedView.
  const viewSpectrumRef = useRef<Float32Array | null>(null);
  const viewSpectrumHiRef = useRef<Float32Array | null>(null);
  const viewPitchRef = useRef<PitchResult | null>(null);
  const extraTracesRef = useRef<ExtraTrace[]>([]);
  const formantsRef = useRef<number[] | null>(null);

  // Loaded track playback speed (pitch preserved), for slowing a song down to learn it.
  const SPEEDS = [1, 0.85, 0.75, 0.6, 0.5];
  const [trackRate, setTrackRate] = useState(1);
  const trackRateRef = useRef(1);
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [playingIds, setPlayingIds] = useState<ReadonlySet<string>>(new Set());
  const [pianoVoice, setPianoVoice] = useState<VoiceType>('sine');
  const [gearOpen, setGearOpen] = useState(false);
  const gearMenuRef = useRef<HTMLDivElement>(null);
  const [referenceRecordingId, setReferenceRecordingId] = useState<string | null>(null);
  // Playback ghost: while a reference take is playing back, its contour is
  // drawn on the spectrogram and live pitch is scored against it.
  const playbackGhostRef = useRef<{ pitches: PitchSample[]; audio: HTMLAudioElement } | null>(null);
  const [singAlongActive, setSingAlongActive] = useState(false);
  const [singScore, setSingScore] = useState<{ accuracy: number; currentCents: number | null; total: number } | null>(null);
  const singFramesRef = useRef({ total: 0, inBand: 0 });

  // Piano
  const [heldPianoKeys, setHeldPianoKeys] = useState<ReadonlySet<number>>(new Set());
  const pianoKeysRef = useRef<ReadonlySet<number>>(new Set());
  const [hoveredKey, setHoveredKey] = useState<number | null>(null);
  const hoveredKeyRef = useRef<number | null>(null);

  // Practice sequences
  const [practiceSchemes, setPracticeSchemes] = useState<PracticeScheme[]>([]);
  const [isRecordingSeq, setIsRecordingSeq] = useState(false);
  const isRecordingSeqRef = useRef(false);
  const seqStartRef = useRef(0);
  const seqEventsRef = useRef<PianoEvent[]>([]);

  // Sequence playback
  const [activeSchemeId, setActiveSchemeId] = useState<string | null>(null);
  const [sessionLooping, setSessionLooping] = useState(true);
  const sessionLoopingRef = useRef(true);
  const [sessionSchemeId, setSessionSchemeId] = useState<string | null>(null);
  const sessionSchemeIdRef = useRef<string | null>(null);
  const activeSchemeRef = useRef<{ segments: NoteSegment[]; totalDurationMs: number } | null>(null);
  const schemePlayStartMsRef = useRef<number | null>(null);
  const seqAudioCtxRef = useRef<AudioContext | null>(null);
  const seqSchedulerRef = useRef<number | null>(null);
  const seqFirstAudioStartRef = useRef(0);
  const seqIterCountRef = useRef(0);
  const seqScheduledUntilRef = useRef(0);
  const activeSchemeDataRef = useRef<PracticeScheme | null>(null);

  // Metronome
  const [bpm, setBpm] = useState(120);
  const bpmRef = useRef(120);
  const [timeSig, setTimeSig] = useState({ num: 4, den: 4 });
  const timeSigRef = useRef({ num: 4, den: 4 });
  const [metronomeActive, setMetronomeActive] = useState(false);
  const metronomeActiveRef = useRef(false);
  const [currentBeat, setCurrentBeat] = useState(0);
  const metroTickCountRef = useRef(0);
  const metroStartMsRef = useRef<number | null>(null);
  const metronomeIntervalRef = useRef<number | null>(null);
  const metroAudioCtxRef = useRef<AudioContext | null>(null);

  const engineRef = useRef<AudioEngine | null>(null);
  const spectrumRef    = useRef<Float32Array | null>(null);
  const spectrumHiRef  = useRef<Float32Array | null>(null);
  const timeDomainRef  = useRef<Float32Array | null>(null);
  const pitchRef       = useRef<PitchResult | null>(null);
  const historyRef     = useRef<(PitchResult | null)[]>(Array(HISTORY_LEN).fill(null));
  const historyIdxRef  = useRef(0);
  const isRecordingRef = useRef(false);
  // Write: pitch samples gathered while writing is on, turned into notes and tab by WritePanel
  const [writeOpen, setWriteOpen] = useState(false);
  const [writing, setWriting] = useState(false);
  const writingRef = useRef(false);
  const writeLiveRef = useRef<PitchSample[]>([]);
  const recPitchesRef  = useRef<PitchSample[]>([]);
  const recStartRef    = useRef(0);
  const ghostPitchesRef = useRef<PitchSample[] | null>(null);

  const recordingsRef = useRef<Recording[]>([]);
  const hasLoadedRef = useRef(false);
  const playersRef = useRef<Map<string, { audio: HTMLAudioElement; url: string }>>(new Map());
  const rangeMenuRef = useRef<HTMLDivElement>(null);

  const handlePitch = useCallback((p: PitchResult | null) => {
    historyRef.current[historyIdxRef.current] = p;
    historyIdxRef.current = (historyIdxRef.current + 1) % HISTORY_LEN;
    pitchRef.current = p;
    if (isRecordingRef.current) {
      recPitchesRef.current.push({ t: performance.now() - recStartRef.current, p });
    }
    if (writingRef.current && writeLiveRef.current.length < 100000) {
      writeLiveRef.current.push({ t: performance.now(), p });
    }
  }, []);

  const handleSpectrum = useCallback((mag: Float32Array) => {
    spectrumRef.current = mag;
    // onPitch has already run for this frame, so pitchRef is current.
    framesRef.current?.push(performance.now(), mag, pitchRef.current);
  }, []);
  const handleSpectrumHi = useCallback((mag: Float32Array) => {
    spectrumHiRef.current = mag;
    // The high resolution analyser spans ~340 ms anyway; store every 4th frame.
    if ((hiFrameCounterRef.current++ & 3) === 0) framesHiRef.current?.push(performance.now(), mag, pitchRef.current);
  }, []);
  const handleTimeDomain = useCallback((td: Float32Array) => { timeDomainRef.current = td; }, []);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      setPitch(viewPitchRef.current);
      // Extra sources: their pitch, the interval to the primary, and the roughness between them
      const rts = extraRtRef.current;
      if (rts.size === 0) return;
      const primary = pitchRef.current;
      const primarySpec = spectrumRef.current;
      const next: Record<string, ExtraLive> = {};
      rts.forEach((rt, id) => {
        const p = rt.pitchRef.current;
        let interval: IntervalInfo | null = null;
        let friction = 0;
        if (p && primary) {
          interval = intervalInfo(primary.hz, p.hz);
          if (primarySpec && rt.spectrumRef.current) {
            const r = roughness(
              partialsOf(primarySpec, sampleRate, binCount, primary.hz),
              partialsOf(rt.spectrumRef.current, sampleRate, binCount, p.hz),
            );
            friction = Math.min(1, r * 4); // a semitone lands near 0.75, a fifth near 0.15
          }
        }
        next[id] = { pitch: p, interval, friction };
      });
      setExtraLive(next);
    }, 66);
    return () => clearInterval(id);
  }, [running, sampleRate, binCount]);

  // Keep playing tracks at the chosen speed
  useEffect(() => {
    trackRateRef.current = trackRate;
    playersRef.current.forEach(({ audio }) => { audio.playbackRate = trackRate; });
  }, [trackRate]);

  // Timbre meters: brightness (spectral centroid), breathiness (spectral
  // flatness) and pitch wobble, read from the analysis refs a few times a second.
  useEffect(() => {
    if (!running) { setMeters({ bright: 0, breath: 0, vibrato: 0 }); return; }
    const id = setInterval(() => {
      const spectrum = viewSpectrumRef.current;
      const p = viewPitchRef.current;
      if (!spectrum || !p) { setMeters(m => (m.bright || m.breath || m.vibrato) ? { bright: 0, breath: 0, vibrato: 0 } : m); return; }
      const s = spectralStats(spectrum, sampleRate, binCount);
      const wobble = pitchWobbleCents(historyRef.current, historyIdxRef.current, 40);
      setMeters({
        bright: brightness(s),
        breath: breathiness(s),
        vibrato: wobble === null ? 0 : Math.max(0, Math.min(1, wobble / 100)),
      });
    }, 80);
    return () => clearInterval(id);
  }, [running, sampleRate, binCount]);

  // Sing-along scoring: compares live pitch to the reference contour at the
  // current playback position while the reference take is playing back.
  useEffect(() => {
    if (!running || !singAlongActive) {
      singFramesRef.current = { total: 0, inBand: 0 };
      setSingScore(null);
      return;
    }
    const id = setInterval(() => {
      const ref = playbackGhostRef.current;
      const live = pitchRef.current;
      if (!ref || ref.audio.paused) return;
      const samples = ref.pitches;
      if (!samples.length) return;
      const nowMs = ref.audio.currentTime * 1000;
      // Binary search for nearest sample by time
      let lo = 0, hi = samples.length - 1;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (samples[mid].t < nowMs) lo = mid + 1; else hi = mid;
      }
      if (lo > 0 && Math.abs(samples[lo - 1].t - nowMs) < Math.abs(samples[lo].t - nowMs)) lo -= 1;
      const expected = samples[lo]?.p;
      let currentCents: number | null = null;
      if (expected && expected.hz > 0 && live && live.clarity >= 0.6 && live.hz > 0) {
        currentCents = 1200 * Math.log2(live.hz / expected.hz);
        singFramesRef.current.total++;
        if (Math.abs(currentCents) <= 50) singFramesRef.current.inBand++;
      }
      const total = singFramesRef.current.total;
      const accuracy = total > 0 ? singFramesRef.current.inBand / total : 0;
      setSingScore({ accuracy, currentCents, total });
    }, 50);
    return () => clearInterval(id);
  }, [running, singAlongActive]);

  // Auto-start the mic engine on first mount. The app opens listening; if
  // permission is denied, error surfaces via setError inside start().
  useEffect(() => {
    arrive();
    if (!engineRef.current) start().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close any open menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (rangeMenuRef.current && !rangeMenuRef.current.contains(e.target as Node)) setRangeMenuOpen(false);
      if (gearMenuRef.current && !gearMenuRef.current.contains(e.target as Node)) setGearOpen(false);
      if (inputMenuRef.current && !inputMenuRef.current.contains(e.target as Node)) setInputMenuOpen(false);
      if (sourceMenuRef.current && !sourceMenuRef.current.contains(e.target as Node)
          && !(sourceMenuPopRef.current && sourceMenuPopRef.current.contains(e.target as Node))) setSourceMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => { sessionLoopingRef.current = sessionLooping; }, [sessionLooping]);
  useEffect(() => { recordingsRef.current = recordings; }, [recordings]);

  // ── Tauri persistence ─────────────────────────────────────────────────────

  // Load saved state once on mount
  useEffect(() => {
    if (!IS_TAURI) return;
    (async () => {
      try {
        const seqJson = await invoke<string>('load_sequences');
        const seqs: any[] = JSON.parse(seqJson);
        if (seqs.length > 0) {
          setPracticeSchemes(seqs.map(s => ({ ...s, segments: buildSegments(s.events) })));
        }
      } catch (e) { console.warn('load_sequences failed:', e); }

      try {
        const metaJson = await invoke<string>('load_recordings_meta');
        const meta: any[] = JSON.parse(metaJson);
        if (meta.length > 0) {
          const loaded = await Promise.all(meta.map(async m => {
            try {
              const bytes = await invoke<number[]>('load_recording_audio', { id: m.id });
              const blob = new Blob([new Uint8Array(bytes)], { type: m.mimeType || 'audio/webm' });
              return { ...m, audioBlob: blob } as Recording;
            } catch {
              return { ...m, audioBlob: null } as Recording;
            }
          }));
          setRecordings(loaded);
        }
      } catch (e) { console.warn('load_recordings_meta failed:', e); }

      hasLoadedRef.current = true;
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save sequences whenever they change (after initial load)
  useEffect(() => {
    if (!IS_TAURI || !hasLoadedRef.current) return;
    const data = JSON.stringify(practiceSchemes.map(
      ({ id, label, events, noteCount, totalDurationMs, createdAt, selected }) =>
        ({ id, label, events, noteCount, totalDurationMs, createdAt, selected })
    ));
    invoke('save_sequences', { data }).catch(console.warn);
  }, [practiceSchemes]);

  // Auto-save recording metadata whenever it changes (after initial load)
  useEffect(() => {
    if (!IS_TAURI || !hasLoadedRef.current) return;
    const data = JSON.stringify(recordings.map(
      ({ id, label, duration, createdAt, selected, pitches, mimeType, imported }) =>
        ({ id, label, duration, createdAt, selected, pitches, mimeType, imported })
    ));
    invoke('save_recordings_meta', { data }).catch(console.warn);
  }, [recordings]);

  // ── Sequence playback engine ──────────────────────────────────────────────

  const stopScheme = useCallback(() => {
    if (seqSchedulerRef.current !== null) { clearInterval(seqSchedulerRef.current); seqSchedulerRef.current = null; }
    seqAudioCtxRef.current?.close().catch(() => {});
    seqAudioCtxRef.current = null;
    activeSchemeRef.current = null;
    activeSchemeDataRef.current = null;
    schemePlayStartMsRef.current = null;
    setActiveSchemeId(null);
  }, []);

  const playScheme = useCallback((scheme: PracticeScheme) => {
    stopScheme();
    if (!scheme.segments.length) return;

    let ctx = seqAudioCtxRef.current;
    if (!ctx || ctx.state === 'closed') {
      ctx = new AudioContext();
      seqAudioCtxRef.current = ctx;
    }
    if (ctx.state === 'suspended') ctx.resume();

    const LOOKAHEAD = 0.5;
    const firstAudioStart = ctx.currentTime + 0.1;
    const playStartWallMs = performance.now() + 100;
    const D = scheme.totalDurationMs / 1000;

    activeSchemeRef.current = { segments: scheme.segments, totalDurationMs: scheme.totalDurationMs };
    activeSchemeDataRef.current = scheme;
    schemePlayStartMsRef.current = playStartWallMs;
    seqFirstAudioStartRef.current = firstAudioStart;
    seqIterCountRef.current = 0;
    seqScheduledUntilRef.current = firstAudioStart;
    sessionSchemeIdRef.current = scheme.id;
    setSessionSchemeId(scheme.id);
    setActiveSchemeId(scheme.id);

    const pump = () => {
      const nowAudio = seqAudioCtxRef.current?.currentTime ?? 0;
      while (seqScheduledUntilRef.current < nowAudio + LOOKAHEAD) {
        const iter = seqIterCountRef.current;
        if (!sessionLoopingRef.current && iter > 0) break;
        scheduleNotes(ctx!, scheme.segments, firstAudioStart + iter * D);
        seqScheduledUntilRef.current = firstAudioStart + (iter + 1) * D;
        seqIterCountRef.current++;
        if (!sessionLoopingRef.current) break;
      }
    };

    pump();
    seqSchedulerRef.current = window.setInterval(pump, 100);
  }, [stopScheme]);

  const toggleScheme = (scheme: PracticeScheme) => {
    if (activeSchemeId === scheme.id) stopScheme();
    else playScheme(scheme);
  };

  // ── Metronome ─────────────────────────────────────────────────────────────

  const makeMetroTick = useCallback(() => {
    const ctx = metroAudioCtxRef.current;
    if (!ctx) return;
    const isDown = metroTickCountRef.current % timeSigRef.current.num === 0;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    osc.frequency.value = isDown ? 1400 : 1000;
    osc.connect(gain); gain.connect(ctx.destination);
    const now = ctx.currentTime;
    gain.gain.setValueAtTime(isDown ? 0.38 : 0.2, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + (isDown ? 0.07 : 0.04));
    osc.start(now); osc.stop(now + (isDown ? 0.08 : 0.05));
    setCurrentBeat(metroTickCountRef.current % timeSigRef.current.num);
    metroTickCountRef.current++;
  }, []);

  const stopMetronome = useCallback(() => {
    if (metronomeIntervalRef.current !== null) { clearInterval(metronomeIntervalRef.current); metronomeIntervalRef.current = null; }
    metronomeActiveRef.current = false;
    setMetronomeActive(false);
  }, []);

  const startMetronome = useCallback((newBpm: number) => {
    if (!metroAudioCtxRef.current || metroAudioCtxRef.current.state === 'closed') {
      metroAudioCtxRef.current = new AudioContext();
    }
    if (metroAudioCtxRef.current.state === 'suspended') metroAudioCtxRef.current.resume();
    if (metronomeIntervalRef.current !== null) clearInterval(metronomeIntervalRef.current);
    metroTickCountRef.current = 0;
    metroStartMsRef.current = performance.now();
    makeMetroTick();
    metronomeIntervalRef.current = window.setInterval(makeMetroTick, 60000 / newBpm);
    metronomeActiveRef.current = true;
    setMetronomeActive(true);
  }, [makeMetroTick]);

  const handleBpmChange = (newBpm: number) => {
    bpmRef.current = newBpm;
    setBpm(newBpm);
    if (metronomeActive) startMetronome(newBpm);
  };

  const handleTimeSigChange = (num: number, den: number) => {
    timeSigRef.current = { num, den };
    setTimeSig({ num, den });
    metroTickCountRef.current = 0;
  };

  const toggleMetronome = () => {
    if (metronomeActive) stopMetronome();
    else startMetronome(bpmRef.current);
  };


  // ── Audio engine ──────────────────────────────────────────────────────────

  const start = async (opts?: InputOptions) => {
    setError(null);
    setMicProblem(null);
    const wanted: InputOptions = opts ?? { deviceId: inputId ?? undefined, processing: inputMode === 'voice', channel: inputChannel };
    const open = async (o: InputOptions) => {
      const engine = new AudioEngine({
        onPitch: handlePitch, onSpectrum: handleSpectrum, onTimeDomain: handleTimeDomain, onSpectrumHi: handleSpectrumHi,
      });
      await engine.start(o);
      return engine;
    };
    try {
      let engine: AudioEngine;
      try {
        engine = await open(wanted);
      } catch (e: any) {
        // A remembered device that is unplugged today: fall back to the default input.
        if (wanted.deviceId && (e?.name === 'OverconstrainedError' || e?.name === 'NotFoundError')) {
          engine = await open({ ...wanted, deviceId: undefined });
          setInputId(null);
          saveInputPrefs(null, wanted.processing === false ? 'instrument' : 'voice', wanted.channel);
          setError('Saved input not found; using the default input');
        } else {
          throw e;
        }
      }
      setSampleRate(engine.sampleRate);
      setBinCount(engine.binCount);
      setInputLabel(engine.inputLabel);
      setInputChannels(engine.channelCount);
      // ~30 s at 60 fps; the hi store takes every 4th frame.
      if (!framesRef.current || framesRef.current.bins !== engine.binCount) framesRef.current = new FrameStore(2048, engine.binCount);
      if (!framesHiRef.current || framesHiRef.current.bins !== engine.binCountHi) framesHiRef.current = new FrameStore(512, engine.binCountHi);
      engineRef.current = engine;
      setRunning(true);
      tell('listening', wanted.processing === false ? 'instrument' : 'voice', true);
    } catch (e: any) {
      setMicProblem(micWords(e?.name ?? (navigator.mediaDevices ? undefined : 'NotSupportedError')));
      tell('no mic', e?.name ?? 'error', true);
    }
  };

  // No microphone: let the app listen to a song file instead, so a visitor
  // without one still sees the tracker and spectrogram move.
  const listenToSong = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'audio/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      engineRef.current?.stop();
      engineRef.current = null;
      const engine = new AudioEngine({
        onPitch: handlePitch, onSpectrum: handleSpectrum, onTimeDomain: handleTimeDomain, onSpectrumHi: handleSpectrumHi,
      });
      try {
        await engine.startFromFile(file);
      } catch {
        engine.stop();
        setError('That file would not play. Try an mp3, m4a or wav.');
        return;
      }
      setMicProblem(null);
      setSampleRate(engine.sampleRate);
      setBinCount(engine.binCount);
      setInputLabel(engine.inputLabel);
      setInputChannels(1);
      if (!framesRef.current || framesRef.current.bins !== engine.binCount) framesRef.current = new FrameStore(2048, engine.binCount);
      if (!framesHiRef.current || framesHiRef.current.bins !== engine.binCountHi) framesHiRef.current = new FrameStore(512, engine.binCountHi);
      engineRef.current = engine;
      setRunning(true);
      tell('listening', 'song', true);
    };
    tell('song instead', '', true);
    input.click();
  };

  // Switch device, processing mode or channel: tear the engine down and reopen.
  const switchInput = async (deviceId: string | null, mode: InputMode, channel: number | undefined = inputChannel) => {
    if (isRecordingRef.current) await finishRecording();
    engineRef.current?.stop();
    engineRef.current = null;
    setRunning(false);
    setInputId(deviceId);
    setInputMode(mode);
    setInputChannel(channel);
    saveInputPrefs(deviceId, mode, channel);
    setInputMenuOpen(false);
    await start({ deviceId: deviceId ?? undefined, processing: mode === 'voice', channel });
  };

  // ── Extra sources ─────────────────────────────────────────────────────────

  const syncExtraTraces = useCallback((list: ExtraSource[]) => {
    const rts = extraRtRef.current;
    extraTracesRef.current = list
      .filter(s => rts.has(s.id))
      .map(s => ({ id: s.id, label: s.label, color: s.color, frames: rts.get(s.id)!.frames }));
  }, []);

  const addSource = useCallback(async (src: ExtraSource): Promise<boolean> => {
    const pitchRefX = { current: null as PitchResult | null };
    const spectrumRefX = { current: null as Float32Array | null };
    const frames = new FrameStore(2048, 2048);
    const engine = new AudioEngine({
      onPitch: p => { pitchRefX.current = p; },
      onSpectrum: mag => { spectrumRefX.current = mag; frames.push(performance.now(), mag, pitchRefX.current); },
      onTimeDomain: () => {},
    });
    try {
      await engine.start({ deviceId: src.deviceId ?? undefined, processing: src.processing, channel: src.channel });
    } catch (e: any) {
      setError(`${src.label}: ${e?.message ?? 'could not open input'}`);
      return false;
    }
    extraRtRef.current.set(src.id, { engine, frames, pitchRef: pitchRefX, spectrumRef: spectrumRefX });
    tell('added a source');
    setExtraSources(prev => {
      const list = prev.some(s => s.id === src.id) ? prev : [...prev, src];
      saveExtraSources(list);
      syncExtraTraces(list);
      return list;
    });
    return true;
  }, [syncExtraTraces]);

  const removeSource = (id: string) => {
    extraRtRef.current.get(id)?.engine.stop();
    extraRtRef.current.delete(id);
    setExtraSources(prev => {
      const list = prev.filter(s => s.id !== id);
      saveExtraSources(list);
      syncExtraTraces(list);
      return list;
    });
    setExtraLive(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  const renameSource = (id: string) => {
    const cur = extraSources.find(s => s.id === id);
    if (!cur) return;
    const name = window.prompt('Name this source', cur.label);
    if (!name || !name.trim()) return;
    setExtraSources(prev => {
      const list = prev.map(s => s.id === id ? { ...s, label: name.trim() } : s);
      saveExtraSources(list);
      syncExtraTraces(list);
      return list;
    });
  };

  // The readout strip clips overflow, so the menu is fixed-positioned under the button.
  const [sourceMenuPos, setSourceMenuPos] = useState({ top: 0, left: 0 });
  const openSourceMenu = async (e: React.MouseEvent<HTMLButtonElement>) => {
    const next = !sourceMenuOpen;
    const r = e.currentTarget.getBoundingClientRect();
    setSourceMenuPos({ top: r.bottom + 6, left: r.left });
    setSourceMenuOpen(next);
    setInputMenuOpen(false);
    if (next) {
      try { setInputs(await AudioEngine.listInputs()); } catch { setInputs([]); }
    }
  };

  const submitNewSource = async () => {
    const src: ExtraSource = {
      id: `src-${Date.now()}`,
      label: newSrc.label.trim() || `Source ${extraSources.length + 2}`,
      color: EXTRA_COLORS[extraSources.length % EXTRA_COLORS.length],
      deviceId: newSrc.deviceId,
      channel: newSrc.channel,
      processing: newSrc.processing,
    };
    setSourceMenuOpen(false);
    await addSource(src);
  };

  // Restore remembered extra sources once the primary input is up (permission granted).
  useEffect(() => {
    if (!running || extrasRestoredRef.current) return;
    extrasRestoredRef.current = true;
    const saved = loadExtraSources();
    (async () => { for (const s of saved) await addSource(s); })();
  }, [running, addSource]);

  // Stop every extra engine on unmount
  useEffect(() => () => { extraRtRef.current.forEach(rt => rt.engine.stop()); }, []);

  const togglePause = () => {
    const next = !viewStateRef.current.paused;
    viewStateRef.current = { paused: next, pauseTimeMs: performance.now(), scrubBackMs: 0 };
    setPaused(next);
    setScrubBack(0);
    if (next) tell('paused', '', true);
  };
  const handleScrub = (secondsBack: number) => {
    viewStateRef.current = { ...viewStateRef.current, scrubBackMs: secondsBack * 1000 };
    setScrubBack(secondsBack);
  };
  const cycleSpeed = () => setTrackRate(r => SPEEDS[(SPEEDS.indexOf(r) + 1) % SPEEDS.length]);

  const openInputMenu = async () => {
    const next = !inputMenuOpen;
    setInputMenuOpen(next);
    setRangeMenuOpen(false);
    setGearOpen(false);
    if (next) {
      try { setInputs(await AudioEngine.listInputs()); } catch { setInputs([]); }
    }
  };

  const finishRecording = async (): Promise<void> => {
    const engine = engineRef.current;
    if (!engine || !isRecordingRef.current) return;
    const blob = await engine.stopRecording();
    const duration = (performance.now() - recStartRef.current) / 1000;
    const pitches = recPitchesRef.current.slice();
    isRecordingRef.current = false;
    ghostPitchesRef.current = null;
    const id = `${Date.now()}`;
    setRecordings(prev => [...prev, {
      id, label: `Take ${prev.length + 1}`, pitches,
      audioBlob: blob, duration, createdAt: Date.now(), selected: true,
    }]);
    setIsRecording(false);
    if (IS_TAURI && blob) {
      blob.arrayBuffer().then(buf => {
        invoke('save_recording_audio', { id, data: Array.from(new Uint8Array(buf)) })
          .catch(console.warn);
      });
    }
  };

  const startRecording = () => {
    const engine = engineRef.current;
    if (!engine || isRecordingRef.current) return;
    if (!engine.startRecording()) return;
    recStartRef.current = performance.now();
    tell('recorded');
    recPitchesRef.current = [];
    isRecordingRef.current = true;
    ghostPitchesRef.current = referenceRecordingId
      ? (recordings.find(r => r.id === referenceRecordingId)?.pitches ?? null)
      : null;
    setIsRecording(true);
  };

  // Currently no UI to stop the engine (app is always listening while open).
  // Kept for future mic-off affordance.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const stop = async () => {
    if (isRecordingRef.current) await finishRecording();
    if (isRecordingSeqRef.current) finishSequence();
    stopScheme();
    stopMetronome();
    engineRef.current?.stop();
    engineRef.current = null;
    spectrumRef.current = null; timeDomainRef.current = null; pitchRef.current = null;
    historyRef.current = Array(HISTORY_LEN).fill(null);
    historyIdxRef.current = 0;
    setRunning(false); setPitch(null);
  };

  // ── Recording playback ────────────────────────────────────────────────────

  const stopOnePlayback = useCallback((id: string) => {
    const player = playersRef.current.get(id);
    if (!player) return;
    player.audio.pause();
    URL.revokeObjectURL(player.url);
    playersRef.current.delete(id);
    setPlayingIds(prev => { const s = new Set(prev); s.delete(id); return s; });
    if (playbackGhostRef.current && playbackGhostRef.current.audio === player.audio) {
      playbackGhostRef.current = null;
      setSingAlongActive(false);
    }
  }, []);

  const stopAllPlaybacks = useCallback(() => {
    playersRef.current.forEach(({ audio, url }) => { audio.pause(); URL.revokeObjectURL(url); });
    playersRef.current.clear();
    setPlayingIds(new Set());
  }, []);

  const stopSession = useCallback(() => {
    stopScheme();
    stopAllPlaybacks();
  }, [stopScheme, stopAllPlaybacks]);

  const playSession = useCallback(() => {
    const schemeId = sessionSchemeIdRef.current;
    const scheme = (schemeId ? practiceSchemes.find(s => s.id === schemeId) : null)
      ?? [...practiceSchemes].reverse().find(s => s.selected)
      ?? practiceSchemes[practiceSchemes.length - 1];
    if (scheme) playScheme(scheme);
    recordingsRef.current.filter(r => r.selected && r.audioBlob).forEach(r => {
      if (playersRef.current.has(r.id)) return;
      const url = URL.createObjectURL(r.audioBlob!);
      const audio = new Audio(url);
      audio.playbackRate = trackRateRef.current;
      (audio as any).preservesPitch = true;
      audio.loop = sessionLoopingRef.current;
      playersRef.current.set(r.id, { audio, url });
      setPlayingIds(prev => { const s = new Set(prev); s.add(r.id); return s; });
      if (!sessionLoopingRef.current) audio.onended = () => stopOnePlayback(r.id);
      audio.onerror = () => stopOnePlayback(r.id);
      audio.play().catch(() => stopOnePlayback(r.id));
      if (r.id === referenceRecordingId && r.pitches.length > 0) {
        playbackGhostRef.current = { pitches: r.pitches, audio };
        singFramesRef.current = { total: 0, inBand: 0 };
        setSingAlongActive(true);
      }
    });
  }, [practiceSchemes, playScheme, stopOnePlayback, referenceRecordingId]);

  const togglePlay = (rec: Recording) => {
    if (!rec.audioBlob) return;
    if (playersRef.current.has(rec.id)) { stopOnePlayback(rec.id); return; }
    const url = URL.createObjectURL(rec.audioBlob);
    const audio = new Audio(url);
    audio.playbackRate = trackRateRef.current;
    (audio as any).preservesPitch = true;
    playersRef.current.set(rec.id, { audio, url });
    setPlayingIds(prev => { const s = new Set(prev); s.add(rec.id); return s; });
    audio.onended = () => stopOnePlayback(rec.id);
    audio.onerror = () => stopOnePlayback(rec.id);
    audio.play().catch(() => stopOnePlayback(rec.id));
    // Sing-along: playing the designated reference take activates the playback
    // ghost + live scoring, if the take has an extracted contour.
    if (rec.id === referenceRecordingId && rec.pitches.length > 0) {
      playbackGhostRef.current = { pitches: rec.pitches, audio };
      singFramesRef.current = { total: 0, inBand: 0 };
      setSingAlongActive(true);
    }
  };

  const playSelected = () => {
    recordings.forEach(rec => { if (rec.selected && !playersRef.current.has(rec.id)) togglePlay(rec); });
  };

  const deleteRecording = (id: string) => {
    if (playersRef.current.has(id)) stopOnePlayback(id);
    if (referenceRecordingId === id) setReferenceRecordingId(null);
    setRecordings(prev => prev.filter(r => r.id !== id));
    if (IS_TAURI) invoke('delete_recording_audio', { id }).catch(console.warn);
  };

  const toggleSelected = (id: string) => {
    setRecordings(prev => prev.map(r => r.id === id ? { ...r, selected: !r.selected } : r));
  };

  const toggleReference = (id: string) => {
    setReferenceRecordingId(prev => prev === id ? null : id);
  };

  const importAudioFile = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'audio/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const arrayBuf = await file.arrayBuffer();
        // Temporary context just to read the duration; decode uses a fresh copy
        // because decodeAudioData consumes the buffer.
        const ctx = new AudioContext();
        const decoded = await ctx.decodeAudioData(arrayBuf.slice(0));
        ctx.close();
        const id = `${Date.now()}`;
        const label = file.name.replace(/\.[^.]+$/, '');
        tell('loaded a track', Math.round(decoded.duration) + 's');
        const mimeType = file.type || 'audio/webm';
        const blob = new Blob([arrayBuf], { type: mimeType });
        setRecordings(prev => [...prev, {
          id, label, pitches: [], audioBlob: blob,
          duration: decoded.duration, createdAt: Date.now(),
          selected: true, mimeType, imported: true, contourProgress: 0,
        }]);
        // Auto-set as the loaded reference track so it drives Sing-along + Track play
        setReferenceRecordingId(id);
        if (IS_TAURI) {
          invoke('save_recording_audio', { id, data: Array.from(new Uint8Array(arrayBuf)) })
            .catch(console.warn);
        }
        // Extract contour offline; progress ticks into the take row, final
        // result populates the take's pitches (compatible with ghost overlay).
        extractContour(decoded, (pct) => {
          setRecordings(prev => prev.map(r => r.id === id ? { ...r, contourProgress: pct } : r));
        }).then(pitches => {
          setRecordings(prev => prev.map(r => r.id === id
            ? { ...r, pitches, contourProgress: 1 }
            : r));
        }).catch(err => {
          console.error('Contour extraction failed:', err);
          setRecordings(prev => prev.map(r => r.id === id
            ? { ...r, contourProgress: 1 }
            : r));
        });
      } catch (e) {
        console.error('Import failed:', e);
        setError('Could not decode audio file');
      }
    };
    input.click();
  };

  // ── Piano callbacks ───────────────────────────────────────────────────────

  const handleHeldKeysChange = useCallback((keys: ReadonlySet<number>) => {
    pianoKeysRef.current = keys;
    setHeldPianoKeys(keys);
  }, []);

  const handleHover = useCallback((midi: number | null) => {
    hoveredKeyRef.current = midi;
    setHoveredKey(midi);
  }, []);

  const handleNoteOn = useCallback((midi: number, timeMs: number) => {
    if (!isRecordingSeqRef.current) return;
    seqEventsRef.current.push({ midi, type: 'on', t: timeMs - seqStartRef.current });
  }, []);

  const handleNoteOff = useCallback((midi: number, timeMs: number) => {
    if (!isRecordingSeqRef.current) return;
    seqEventsRef.current.push({ midi, type: 'off', t: timeMs - seqStartRef.current });
  }, []);

  // ── Sequence recording ────────────────────────────────────────────────────

  const startSequence = () => {
    seqStartRef.current = performance.now();
    seqEventsRef.current = [];
    isRecordingSeqRef.current = true;
    setIsRecordingSeq(true);
  };

  const finishSequence = () => {
    const events = seqEventsRef.current.slice();
    const noteCount = events.filter(e => e.type === 'on').length;
    isRecordingSeqRef.current = false;
    if (noteCount > 0) {
      const segments = buildSegments(events);
      const totalDurationMs = segments.length > 0
        ? Math.max(...segments.map(s => s.startMs + s.durationMs)) + 500
        : 0;
      setPracticeSchemes(prev => [...prev, {
        id: `${Date.now()}`, label: `Seq ${prev.length + 1}`,
        events, noteCount, segments, totalDurationMs, createdAt: Date.now(), selected: true,
      }]);
    }
    setIsRecordingSeq(false);
  };

  const deleteScheme = (id: string) => {
    if (activeSchemeId === id) stopScheme();
    if (sessionSchemeId === id) { sessionSchemeIdRef.current = null; setSessionSchemeId(null); }
    setPracticeSchemes(prev => prev.filter(s => s.id !== id));
  };

  const toggleSchemeSelected = (id: string) => {
    setPracticeSchemes(prev => prev.map(s => s.id === id ? { ...s, selected: !s.selected } : s));
  };

  // ── Session import / export ───────────────────────────────────────────────

  const exportSession = () => {
    const data = {
      version: 1, exportedAt: new Date().toISOString(),
      sequences: practiceSchemes.map(({ id, label, events, noteCount, totalDurationMs, createdAt, selected }) =>
        ({ id, label, events, noteCount, totalDurationMs, createdAt, selected })),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `songbird-${Date.now()}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const importSession = () => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = '.json';
    input.onchange = () => {
      const file = input.files?.[0]; if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const data = JSON.parse(ev.target?.result as string);
          if (Array.isArray(data.sequences)) {
            setPracticeSchemes(prev => {
              const existingIds = new Set(prev.map(s => s.id));
              const incoming = (data.sequences as any[])
                .filter(s => !existingIds.has(s.id) && Array.isArray(s.events))
                .map(s => ({ ...s, segments: buildSegments(s.events), selected: s.selected ?? true }));
              return [...prev, ...incoming];
            });
          }
        } catch {}
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const downloadRecording = (rec: Recording) => {
    if (!rec.audioBlob) return;
    const url = URL.createObjectURL(rec.audioBlob);
    const a = document.createElement('a'); a.href = url;
    a.download = `${rec.label.replace(/\s+/g, '-')}.webm`; a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => () => { engineRef.current?.stop(); stopAllPlaybacks(); stopScheme(); stopMetronome(); },
    [stopAllPlaybacks, stopScheme, stopMetronome]);

  const sessionActive = activeSchemeId !== null || playingIds.size > 0;
  const canPlay = practiceSchemes.length > 0 || recordings.some(r => r.selected);

  // Pick the pitch shown in the plate: voice takes priority; else the hovered
  // or most-recently-held piano note; else nothing.
  const heldMidiList = Array.from(heldPianoKeys);
  const pianoMidi = hoveredKey ?? (heldMidiList.length ? heldMidiList[heldMidiList.length - 1] : null);
  const hasVoice = pitch !== null;
  const hasPiano = pianoMidi !== null;
  const voiceKeys = Object.keys(VOICE_LABELS) as VoiceType[];
  const cycleVoice = () => setPianoVoice(v => voiceKeys[(voiceKeys.indexOf(v) + 1) % voiceKeys.length]);
  const loadedTrack = recordings.find(r => r.id === referenceRecordingId) ?? null;
  const loadedTrackPlaying = loadedTrack ? playingIds.has(loadedTrack.id) : false;

  return (
    <div className="stage">
      <div className={`frame${writeOpen ? ' write-open' : ''}`}>

        {/* Readout strip (top) */}
        <div className="readout">
          <div className="voice-block" title="Your live voice: note, frequency, and cents deviation from the nearest semitone.">
            <span className="label">Voice</span>
            {hasVoice ? (
              <>
                <span className="voice-note">{pitch!.note}<sub>{pitch!.octave}</sub></span>
                <span className="voice-hz">{pitch!.hz} Hz</span>
                <span className={`voice-cents ${pitch!.cents > 0 ? 'sharp' : pitch!.cents < 0 ? 'flat' : 'intune'}`}>
                  {pitch!.cents > 0 ? '+' : ''}{pitch!.cents}¢
                </span>
              </>
            ) : (
              <span className="idle">—</span>
            )}
          </div>
          <div className="readout-divider" />
          <div className="piano-block" title="Piano note under your finger or the cursor.">
            <span className="label">Piano</span>
            {hasPiano ? (
              <>
                <span className="piano-note">{NOTE_NAMES[pianoMidi! % 12]}<sub>{Math.floor(pianoMidi! / 12) - 1}</sub></span>
                <span className="piano-hz">{midiToHz(pianoMidi!)} Hz</span>
              </>
            ) : (
              <span className="idle">—</span>
            )}
          </div>

          <div className="readout-spacer sources-strip">
            {extraSources.map(s => {
              const live = extraLive[s.id];
              const p = live?.pitch ?? null;
              const iv = live?.interval ?? null;
              return (
                <div key={s.id} className="source-chip" style={{ ['--src' as any]: s.color }}
                  title={`${s.label}: ${CHANNEL_LABELS[s.channel === undefined ? 'mix' : String(s.channel)]} · ${s.processing ? 'voice cleanup' : 'raw signal'}. Click the name to rename. The interval is relative to the main input.`}>
                  <span className="src-dot" />
                  <button className="src-label" onClick={() => renameSource(s.id)}>{s.label}</button>
                  {p ? (
                    <>
                      <span className="src-note">{p.note}<sub>{p.octave}</sub></span>
                      <span className="src-hz">{p.hz} Hz</span>
                      {iv && (
                        <span className="src-interval">
                          {iv.direction === 'same' ? 'unison' : `${iv.name} ${iv.direction === 'above' ? '↑' : '↓'}`}
                          <span className="src-cents"> {iv.centsEqual > 0 ? '+' : ''}{iv.centsEqual}¢</span>
                        </span>
                      )}
                      {iv && (
                        <span className="src-blend" title="Blend: how smoothly the two sounds' overtones sit together right now. Full = smooth, empty = beating and rough.">
                          <span className="src-blend-fill" style={{ width: `${Math.round((1 - live.friction) * 100)}%` }} />
                        </span>
                      )}
                    </>
                  ) : <span className="idle">—</span>}
                  <button className="src-remove" onClick={() => removeSource(s.id)} title="Remove this source">✕</button>
                </div>
              );
            })}
            <div className="picker-wrap" ref={sourceMenuRef}>
              <button className="btn-add-source" onClick={openSourceMenu} title="Add another live input: a second mic, or the other input of your audio interface. Each source gets its own colour on the tracker and an interval readout against the main input.">＋ Source</button>
              {sourceMenuOpen && createPortal(
                <div ref={sourceMenuPopRef} className="picker-menu source-menu" style={{ top: sourceMenuPos.top, left: sourceMenuPos.left }} onClick={e => e.stopPropagation()}>
                  <div className="picker-menu-head">Name</div>
                  <div className="source-form-row">
                    {['Guitar', 'Voice 2', 'Keys', 'Other'].map(n => (
                      <button key={n} className={newSrc.label === n ? 'active' : ''} onClick={() => setNewSrc(v => ({ ...v, label: n }))}>{n}</button>
                    ))}
                  </div>
                  <div className="picker-menu-head">Device</div>
                  <button className={newSrc.deviceId === null ? 'active' : ''} onClick={() => setNewSrc(v => ({ ...v, deviceId: null }))}>System default</button>
                  {inputs.filter(d => d.deviceId && d.deviceId !== 'default' && d.deviceId !== 'communications').map(d => (
                    <button key={d.deviceId} className={newSrc.deviceId === d.deviceId ? 'active' : ''} title={d.label}
                      onClick={() => setNewSrc(v => ({ ...v, deviceId: d.deviceId }))}>{d.label || `Input ${d.deviceId.slice(0, 6)}`}</button>
                  ))}
                  <div className="picker-menu-head">Channel</div>
                  <div className="source-form-row">
                    {([undefined, 0, 1] as (number | undefined)[]).map(c => (
                      <button key={String(c)} className={newSrc.channel === c ? 'active' : ''} onClick={() => setNewSrc(v => ({ ...v, channel: c }))}>
                        {CHANNEL_LABELS[c === undefined ? 'mix' : String(c)]}
                      </button>
                    ))}
                  </div>
                  <div className="picker-menu-head">Mode</div>
                  <div className="source-form-row">
                    <button className={!newSrc.processing ? 'active' : ''} onClick={() => setNewSrc(v => ({ ...v, processing: false }))}>Instrument · raw</button>
                    <button className={newSrc.processing ? 'active' : ''} onClick={() => setNewSrc(v => ({ ...v, processing: true }))}>Voice · cleaned</button>
                  </div>
                  <div className="source-form-row source-form-submit">
                    <button className="btn-add-source" onClick={submitNewSource}>Add source</button>
                  </div>
                </div>,
                document.body,
              )}
            </div>
          </div>

          <button
            className={`btn-pause${paused ? ' on' : ''}`}
            onClick={togglePause}
            title={paused ? 'Resume the live display' : 'Freeze the display. The mic keeps listening; scrub back up to 30 s. Hover the tracker to inspect any slice.'}
          >{paused ? '▶ Live' : '⏸ Pause'}</button>
          <div className="scrub" title="Scrub back through the last 30 seconds (pause first). Right end = the moment you paused.">
            <span className="scrub-label">Scrub</span>
            <input
              type="range" min={0} max={30} step={0.05}
              value={30 - scrubBack}
              disabled={!paused}
              onChange={e => handleScrub(30 - Number(e.target.value))}
            />
            <span className="scrub-time">-{scrubBack.toFixed(1)}s</span>
          </div>
        </div>

        {/* Slim piano row */}
        <div className="piano-row">
          <div className="piano-cheek" title="Piano covers C2 to B6 (43 white keys total)">Piano · C2–B6</div>
          <div className="piano-slot">
            <Piano
              currentPitch={running ? pitch : null}
              voice={pianoVoice}
              onHeldKeysChange={handleHeldKeysChange}
              onNoteOn={handleNoteOn}
              onNoteOff={handleNoteOff}
              onHoverChange={handleHover}
            />
          </div>
          <div className="piano-cheek" title="Click Voice in the toolbar to cycle synths">Voice · {VOICE_LABELS[pianoVoice]}</div>
        </div>

        {/* Main deck: tracker + right rail */}
        <div className="deck">
          <div className="tracker" title="Spectrogram + pitch trace. Voice = green. Loaded reference = magenta. Piano key = chartreuse. Hover = cerulean.">
            <CombinedView
              framesRef={framesRef}
              framesHiRef={framesHiRef}
              spectrumRef={spectrumRef}
              spectrumHiRef={spectrumHiRef}
              pitchRef={pitchRef}
              viewStateRef={viewStateRef}
              viewSpectrumRef={viewSpectrumRef}
              viewSpectrumHiRef={viewSpectrumHiRef}
              viewPitchRef={viewPitchRef}
              sampleRate={sampleRate}
              binCount={binCount}
              rangeMode={rangeMode}
              extraTracesRef={extraTracesRef}
              formantsRef={formantsRef}
              pianoKeysRef={pianoKeysRef}
              ghostPitchesRef={ghostPitchesRef}
              recStartMsRef={recStartRef}
              activeSchemeRef={activeSchemeRef}
              schemePlayStartMsRef={schemePlayStartMsRef}
              schemeLoopingRef={sessionLoopingRef}
              metronomeActiveRef={metronomeActiveRef}
              bpmRef={bpmRef}
              timeSigRef={timeSigRef}
              metroStartMsRef={metroStartMsRef}
              hoveredKeyRef={hoveredKeyRef}
              playbackGhostRef={playbackGhostRef}
            />
            {singAlongActive && singScore && (
              <div className="sing-score-overlay">
                <span className="sing-score-label">↺ Sing-along</span>
                <span className="sing-score-acc">{Math.round(singScore.accuracy * 100)}%</span>
                <span className="sing-score-cents">
                  {singScore.currentCents === null
                    ? '—'
                    : `${singScore.currentCents > 0 ? '+' : ''}${Math.round(singScore.currentCents)}¢`}
                </span>
              </div>
            )}
          </div>

          <div className="rail">
            <div className="rail-panel" title="The recipe of your current sound: fundamental (1x) plus overtones (2x 3x 4x etc). Their relative heights are what makes a violin sound different from a voice at the same pitch.">
              <div className="rail-title">
                <h4>Harmonic Recipe</h4>
                <small>1× fundamental · 2× 3× … overtones</small>
              </div>
              <HarmonicRecipe spectrumRef={viewSpectrumRef} pitchRef={viewPitchRef} sampleRate={sampleRate} binCount={binCount} />
            </div>

            <div className="rail-panel" title="Which of the twelve notes are ringing right now, regardless of octave. One tall bar = a single note. Several = a chord, and Songbird names its best guess. The outlined bar is the note the pitch tracker is locked on.">
              <div className="rail-title">
                <h4>Notes Ringing</h4>
                <small>chord guess</small>
              </div>
              <NotesRinging spectrumRef={viewSpectrumHiRef} pitchRef={viewPitchRef} sampleRate={sampleRate} />
            </div>

            <div className="rail-panel" title="Formants: the resonances of your throat and mouth, not your vocal folds. F1 and F2 place the vowel (the chart), F3 and up are the ring. Same note, different formants = a different voice. Live only; this does not pause.">
              <div className="rail-title">
                <h4>Formants</h4>
                <small>vowel chart · live</small>
              </div>
              <FormantsPanel timeDomainRef={timeDomainRef} pitchRef={pitchRef} sampleRate={sampleRate} formantsOutRef={formantsRef} />
            </div>

            <div className="rail-panel" title="Waveform (raw shape of the current wave) plus timbre meters: brightness = high-harmonic energy, breathiness = non-harmonic noise, vibrato = pitch wobble.">
              <div className="rail-title">
                <h4>Timbre</h4>
                <small>waveform + meters</small>
              </div>
              <div className="osc" title="Live waveform shape (oscilloscope).">
                <RadialScope timeDomainRef={timeDomainRef} />
              </div>
              <div className="timbre-meters">
                <div className="meter" title="Brightness: where the energy sits. Left = dark and round, right = bright and edgy."><span className="meter-lbl">Bright</span><div className="meter-bar"><div className="meter-fill bright" style={{ width: `${Math.round(meters.bright * 100)}%` }} /></div></div>
                <div className="meter" title="Breathiness: how much of the sound is noise rather than pitched harmonics."><span className="meter-lbl">Breath</span><div className="meter-bar"><div className="meter-fill breath" style={{ width: `${Math.round(meters.breath * 100)}%` }} /></div></div>
                <div className="meter" title="Pitch wobble over the last two thirds of a second, in cents. Full = ±100¢ (a whole semitone each way)."><span className="meter-lbl">Vibrato</span><div className="meter-bar"><div className="meter-fill vibrato" style={{ width: `${Math.round(meters.vibrato * 100)}%` }} /></div></div>
              </div>
            </div>
          </div>
        </div>

        {writeOpen && (
          <WritePanel
            liveRef={writeLiveRef}
            writing={writing}
            onToggleWriting={() => {
              const next = !writingRef.current;
              writingRef.current = next; setWriting(next);
              if (next) tell('wrote', 'started', true);
            }}
            onClear={() => { writeLiveRef.current = []; }}
            takes={recordings.filter(r => r.pitches.length > 0).map(r => ({ id: r.id, label: r.label, pitches: r.pitches }))}
            onWrote={n => tell('kept tab', n + ' notes')}
          />
        )}

        {/* Bottom toolbar */}
        <div className="toolbar">
          <button
            className={`tbtn rec${isRecording ? ' recording' : ''}`}
            onClick={isRecording ? finishRecording : startRecording}
            disabled={!running && !isRecording}
            title={isRecording ? 'Stop this take' : 'Capture what the mic is hearing'}
          >{isRecording ? '◼ Stop' : '● Rec'}</button>

          <button
            className="tbtn load"
            onClick={importAudioFile}
            title="Load an audio file (mp3 wav m4a) to compare with. Becomes the reference track."
          >＋ Load audio</button>

          <button
            className={`tbtn load${writeOpen ? ' playing' : ''}`}
            onClick={() => setWriteOpen(o => { if (o) { writingRef.current = false; setWriting(false); } return !o; })}
            title="Write: mess around and Songbird writes the notes and the guitar tab for you. Copy or save it."
          >✎ Write</button>

          {loadedTrack && (
            <button
              className={`tbtn play${loadedTrackPlaying ? ' playing' : ''}`}
              onClick={() => togglePlay(loadedTrack)}
              disabled={!loadedTrack.audioBlob}
              title={loadedTrackPlaying ? 'Stop the loaded track' : 'Play the loaded track (sing-along)'}
            >{loadedTrackPlaying ? '◼ Stop' : '▶ Track'}</button>
          )}
          {loadedTrack && (
            <button
              className="picker"
              onClick={cycleSpeed}
              title="Playback speed for the loaded track, pitch preserved. Click to cycle. Slow it down to learn it."
            >Speed<span className="val">×{trackRate}</span></button>
          )}

          <div className="track-status" title="Currently loaded reference track">
            {loadedTrack
              ? <>Loaded · <strong>{loadedTrack.label}</strong>{loadedTrack.contourProgress !== undefined && loadedTrack.contourProgress < 1 && <span className="contour-progress"> · analyzing {Math.round(loadedTrack.contourProgress * 100)}%</span>}</>
              : <span className="track-status-empty">No track loaded</span>
            }
          </div>

          <InlineMetro bpm={bpm} active={metronomeActive} onBpmChange={handleBpmChange} onToggle={toggleMetronome} />

          <div className="picker-wrap" ref={inputMenuRef}>
            <button
              className="picker"
              onClick={openInputMenu}
              title={`Which input Songbird listens to, and how. Voice mode cleans up echo and noise; Instrument mode passes the raw signal (use it for guitar or an audio interface). Now: ${inputLabel || 'default input'}`}
            >Input<span className="val">{inputMode === 'voice' ? 'Voice' : 'Inst'}{inputLabel ? ` · ${shortDeviceName(inputLabel)}` : ''}</span></button>
            {inputMenuOpen && (
              <div className="picker-menu input-menu">
                <div className="picker-menu-head">Device</div>
                <button
                  className={inputId === null ? 'active' : ''}
                  onClick={() => switchInput(null, inputMode)}
                >System default</button>
                {inputs.filter(d => d.deviceId && d.deviceId !== 'default' && d.deviceId !== 'communications').map(d => (
                  <button
                    key={d.deviceId}
                    className={inputId === d.deviceId ? 'active' : ''}
                    onClick={() => switchInput(d.deviceId, inputMode)}
                    title={d.label}
                  >{d.label || `Input ${d.deviceId.slice(0, 6)}`}</button>
                ))}
                {inputs.length === 0 && <div className="picker-menu-note">No devices listed yet. Allow the microphone once, then reopen.</div>}
                <div className="picker-menu-head">Channel{inputChannels > 1 ? ` · device has ${inputChannels}` : ''}</div>
                {([undefined, 0, 1] as (number | undefined)[]).map(c => (
                  <button
                    key={String(c)}
                    className={inputChannel === c ? 'active' : ''}
                    onClick={() => switchInput(inputId, inputMode, c)}
                    title={c === undefined ? 'Analyse the mono mix of all channels' : `Analyse only channel ${c + 1} of a multi-channel input (an interface exposes its inputs as channels)`}
                  >{CHANNEL_LABELS[c === undefined ? 'mix' : String(c)]}</button>
                ))}
                <div className="picker-menu-head">Mode</div>
                <button
                  className={inputMode === 'voice' ? 'active' : ''}
                  onClick={() => switchInput(inputId, 'voice')}
                  title="Echo cancellation + noise suppression on. Best for singing at a mic while the piano plays through speakers."
                >Voice · cleaned up</button>
                <button
                  className={inputMode === 'instrument' ? 'active' : ''}
                  onClick={() => switchInput(inputId, 'instrument')}
                  title="No browser processing. Best for guitar, line inputs, and audio interfaces like the Scarlett."
                >Instrument · raw signal</button>
              </div>
            )}
          </div>

          <button
            className="picker"
            onClick={cycleVoice}
            title={`Piano voice — click to cycle through the nine synths. Current: ${VOICE_LABELS[pianoVoice]}`}
          >Voice<span className="val">{VOICE_LABELS[pianoVoice]}</span></button>

          <div className="picker-wrap" ref={rangeMenuRef}>
            <button
              className="picker"
              onClick={() => setRangeMenuOpen(o => !o)}
              title="Frequency range the tracker displays"
            >Range<span className="val">{rangeMode}</span></button>
            {rangeMenuOpen && (
              <div className="picker-menu">
                {(['wide', 'standard', 'vocal', 'guitar', 'follow'] as RangeMode[]).map(m => (
                  <button
                    key={m}
                    className={rangeMode === m ? 'active' : ''}
                    onClick={() => { setRangeMode(m); setRangeMenuOpen(false); }}
                  >{m}</button>
                ))}
              </div>
            )}
          </div>

          <div className="gear-wrap" ref={gearMenuRef}>
            <button
              className="tbtn gear"
              onClick={() => setGearOpen(o => !o)}
              title="Session import / export"
            >⚙</button>
            {gearOpen && (
              <div className="gear-menu">
                <button onClick={() => { exportSession(); setGearOpen(false); }} disabled={practiceSchemes.length === 0}>↓ Export session</button>
                <button onClick={() => { importSession(); setGearOpen(false); }}>↑ Import session</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="error-toast" onClick={() => setError(null)}>{error} ✕</div>
      )}

      {micProblem && (
        <div className="mic-note" role="alert">
          <p>{micProblem}</p>
          <div className="mic-note-buttons">
            <button className="tbtn" onClick={listenToSong}>Listen to a song instead</button>
            <button className="tbtn" onClick={() => { tell('mic retry', '', true); start(); }}>Try again</button>
            <button className="tbtn" onClick={() => setMicProblem(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
